import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X, Play, Pause, ChevronLeft, ChevronRight, Copy, CheckCircle, Sparkles } from "lucide-react";
import { TOUR_STEPS, DEMO_TOKEN } from "./tourSteps";

const AUTO_ADVANCE_MS = 10000;

export default function InvestorTour({ onClose }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [copied, setCopied] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const progressRef = useRef(null);
  const tickRef = useRef(null);
  const step = TOUR_STEPS[stepIdx];

  // Navigate when step changes
  useEffect(() => {
    if (location.pathname !== step.route) navigate(step.route);
  }, [stepIdx, step.route, navigate, location.pathname]);

  // Find anchor element for callout positioning
  useEffect(() => {
    const findAnchor = () => {
      const selectors = step.anchor.split(",").map((s) => s.trim());
      for (const sel of selectors) {
        const el = document.querySelector(sel);
        if (el) {
          const r = el.getBoundingClientRect();
          setAnchorRect({ top: r.top, left: r.left, width: r.width, height: r.height });
          return;
        }
      }
      setAnchorRect(null);
    };
    const t = setTimeout(findAnchor, 400);
    window.addEventListener("resize", findAnchor);
    return () => { clearTimeout(t); window.removeEventListener("resize", findAnchor); };
  }, [stepIdx, step.anchor]);

  // Auto-advance with progress bar
  useEffect(() => {
    if (!playing) { if (tickRef.current) clearInterval(tickRef.current); return; }
    setProgress(0);
    const start = Date.now();
    tickRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, (elapsed / AUTO_ADVANCE_MS) * 100);
      setProgress(pct);
      if (pct >= 100) {
        clearInterval(tickRef.current);
        setStepIdx((i) => (i + 1) % TOUR_STEPS.length);
      }
    }, 80);
    return () => tickRef.current && clearInterval(tickRef.current);
  }, [stepIdx, playing]);

  const next = useCallback(() => setStepIdx((i) => Math.min(TOUR_STEPS.length - 1, i + 1)), []);
  const prev = useCallback(() => setStepIdx((i) => Math.max(0, i - 1)), []);

  const copyShareLink = () => {
    const url = `${window.location.origin}/investor-demo?token=${DEMO_TOKEN}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Keyboard controls
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
      if (e.key === " ") { e.preventDefault(); setPlaying((p) => !p); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, onClose]);

  return (
    <>
      {/* Dim overlay — covers everything except anchored element spotlight */}
      <div className="fixed inset-0 z-[100] pointer-events-none" data-testid="investor-tour-overlay">
        <div className="absolute inset-0 bg-black/60 transition-opacity" />
        {anchorRect && (
          <div
            className="absolute border-2 border-[#22C55E] rounded-sm transition-all duration-500 ease-out shadow-[0_0_0_9999px_rgba(5,10,7,0.65)] pointer-events-none"
            style={{
              top: Math.max(4, anchorRect.top - 6),
              left: Math.max(4, anchorRect.left - 6),
              width: anchorRect.width + 12,
              height: anchorRect.height + 12,
            }}
          />
        )}
      </div>

      {/* Callout pill — top center */}
      <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[101] bg-[#09120D] border border-[#22C55E]/40 shadow-2xl px-5 py-3 max-w-xl pointer-events-auto" data-testid="investor-tour-callout">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-3.5 h-3.5 text-[#22C55E]" />
          <span className="text-[#22C55E] text-[10px] font-semibold uppercase tracking-[0.2em]">
            Step {stepIdx + 1} / {TOUR_STEPS.length}
          </span>
          <span className="text-[#A1B2A8]/40 text-[10px]">· {step.id}</span>
        </div>
        <p className="text-white font-['Outfit'] text-base font-semibold leading-snug">{step.title}</p>
        <p className="text-[#A1B2A8] text-xs mt-1">{step.narration}</p>
      </div>

      {/* Right drawer — script + controls */}
      <aside className="fixed top-0 right-0 h-full w-[380px] z-[102] bg-[#050A07] border-l border-[#1A2E22] flex flex-col pointer-events-auto" data-testid="investor-tour-drawer">
        <div className="px-5 py-4 border-b border-[#1A2E22] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#22C55E]" />
            <span className="font-['Outfit'] font-semibold text-white text-sm">Investor Mode</span>
          </div>
          <button onClick={onClose} className="text-[#A1B2A8] hover:text-white" data-testid="investor-tour-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress */}
        <div className="px-5 pt-4">
          <div className="h-0.5 bg-[#1A2E22] relative overflow-hidden">
            <div ref={progressRef} className="h-full bg-[#22C55E] transition-[width] duration-100" style={{ width: `${progress}%` }} />
          </div>
          <div className="flex items-center justify-between mt-2 text-[10px] text-[#A1B2A8]/60 uppercase tracking-wider">
            <span>Auto-advance {playing ? "on" : "paused"}</span>
            <span>{stepIdx + 1} of {TOUR_STEPS.length}</span>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <p className="text-[#22C55E] text-[10px] font-semibold uppercase tracking-[0.2em] mb-2">What to say</p>
          <p className="text-[#E8F3EC] text-sm leading-relaxed">{step.script}</p>

          {step.highlights && (
            <div className="mt-5">
              <p className="text-[#A1B2A8] text-[10px] font-semibold uppercase tracking-[0.2em] mb-2">Key numbers</p>
              <ul className="space-y-1.5">
                {step.highlights.map((h, i) => (
                  <li key={i} className="text-[#A1B2A8] text-xs flex items-start gap-2">
                    <span className="text-[#22C55E] mt-0.5">▸</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Step jump list */}
          <div className="mt-6">
            <p className="text-[#A1B2A8] text-[10px] font-semibold uppercase tracking-[0.2em] mb-2">Jump to step</p>
            <div className="grid grid-cols-1 gap-1">
              {TOUR_STEPS.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => setStepIdx(i)}
                  data-testid={`tour-step-${s.id}`}
                  className={`text-left text-xs px-3 py-2 border transition-colors ${
                    i === stepIdx
                      ? "border-[#22C55E]/50 bg-[#22C55E]/10 text-[#22C55E]"
                      : "border-[#1A2E22] text-[#A1B2A8] hover:text-white"
                  }`}
                >
                  <span className="font-mono text-[10px] mr-2">{String(i + 1).padStart(2, "0")}</span>
                  {s.title.replace(/^Part \d+ — /, "")}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="border-t border-[#1A2E22] px-5 py-4 space-y-3">
          <div className="flex items-center gap-2">
            <button onClick={prev} disabled={stepIdx === 0}
              className="flex-1 px-3 py-2 border border-[#1A2E22] text-[#A1B2A8] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-1 text-xs"
              data-testid="investor-tour-prev">
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <button onClick={() => setPlaying((p) => !p)}
              className="px-4 py-2 bg-[#22C55E]/10 border border-[#22C55E]/40 text-[#22C55E] hover:bg-[#22C55E]/20 flex items-center justify-center gap-1 text-xs"
              data-testid="investor-tour-toggle">
              {playing ? <><Pause className="w-4 h-4" /> Pause</> : <><Play className="w-4 h-4" /> Play</>}
            </button>
            <button onClick={next} disabled={stepIdx === TOUR_STEPS.length - 1}
              className="flex-1 px-3 py-2 border border-[#1A2E22] text-[#A1B2A8] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-1 text-xs"
              data-testid="investor-tour-next">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <button onClick={copyShareLink}
            className="w-full px-3 py-2 border border-[#22C55E]/30 bg-[#22C55E]/5 text-[#22C55E] hover:bg-[#22C55E]/10 flex items-center justify-center gap-2 text-xs"
            data-testid="investor-tour-copy-link">
            {copied ? <><CheckCircle className="w-4 h-4" /> Link copied</> : <><Copy className="w-4 h-4" /> Copy shareable demo link</>}
          </button>
          <p className="text-[#A1B2A8]/40 text-[10px] text-center leading-snug">
            Shareable link → <span className="text-[#A1B2A8]">/investor-demo</span> · public · tokenized · read-only
          </p>
        </div>
      </aside>
    </>
  );
}
